import React from 'react'
import { CountChip } from 'src/components/Reusable/Chips/BasicChip'
import { useParams } from 'react-router-dom'
import { openInNewTab } from 'src/Utils/globalHelpers'
import { EvidenceToControlMappingInfo } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { generatePermalinkFromId } from 'src/components/PageLayout/Permalink'
import { EvidenceGroup } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  MappingLinkButton,
  MappingsContainer,
  MappingsSection,
  MappingsSectionHeader,
  MappingsSectionItem,
} from 'src/components/Reusable/Mappings/Mappings.styles'
import {
  EvidenceControlMappingLinkControlsButton,
  EvidenceControlMappingUnlinkControlsButton,
} from './EvidenceControlMappings.components'

const EvidenceControlMappingControlItem = ({
  control,
  evidence,
  isSuggestion = false,
}: {
  control: Control
  evidence: EvidenceGroup
  isSuggestion?: boolean
}): JSX.Element => {
  const { pageContext } = useParams()

  const permalink = generatePermalinkFromId({
    pageContext: pageContext as string,
    modelId: control.getId(),
    modelType: MODEL_TYPE.CONTROL,
    isInternalLink: true,
  })

  return (
    <MappingsSectionItem key={evidence.getId()}>
      {isSuggestion ? (
        <EvidenceControlMappingLinkControlsButton
          evidence={evidence}
          controls={[control]}
          tooltipText="Link control to evidence"
        />
      ) : (
        <EvidenceControlMappingUnlinkControlsButton
          evidence={evidence}
          controls={[control]}
          tooltipText="Unlink control from evidence"
        />
      )}
      {control.getModelId()} {control.getName()}
      <MappingLinkButton
        onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
          e.stopPropagation()
          e.preventDefault()
          openInNewTab(permalink)
        }}
      />
    </MappingsSectionItem>
  )
}

export const EvidenceControlMappingLinkedControls = ({
  mapping,
  evidence,
}: {
  mapping: EvidenceToControlMappingInfo
  evidence: EvidenceGroup
}): JSX.Element => {
  const allowUnlink = mapping.getLinkedControlsList().length > 0
  return (
    <>
      <MappingsSectionHeader>
        <EvidenceControlMappingUnlinkControlsButton
          controls={mapping.getLinkedControlsList()}
          evidence={evidence}
          tooltipText="Unlink all controls from evidence"
          isDisabled={!allowUnlink}
        />
        Linked Controls
        <CountChip inline={true}>
          {mapping.getLinkedControlsList().length}
        </CountChip>
      </MappingsSectionHeader>
      <MappingsSection>
        {mapping.getLinkedControlsList().length > 0 ? (
          <>
            {mapping.getLinkedControlsList().map((control) => (
              <EvidenceControlMappingControlItem
                key={`linked-control-${control.getId()}`}
                control={control}
                evidence={evidence}
              />
            ))}
          </>
        ) : (
          <MappingsSectionItem>
            No controls linked to this evidence
          </MappingsSectionItem>
        )}
      </MappingsSection>
    </>
  )
}

export const EvidenceControlMappingSuggestedControls = ({
  mapping,
  evidence,
}: {
  mapping: EvidenceToControlMappingInfo
  evidence: EvidenceGroup
}): JSX.Element => {
  return (
    <>
      <MappingsSectionHeader>
        <EvidenceControlMappingLinkControlsButton
          controls={mapping.getSuggestedControlsList()}
          evidence={evidence}
          tooltipText="Link all suggested controls to evidence"
        />
        Suggested Controls
        <CountChip inline={true}>
          {mapping.getSuggestedControlsList().length}
        </CountChip>
      </MappingsSectionHeader>
      <MappingsSection>
        {mapping.getSuggestedControlsList().length > 0 ? (
          <>
            {mapping.getSuggestedControlsList().map((control) => (
              <EvidenceControlMappingControlItem
                key={`suggested-control-${control.getId()}`}
                control={control}
                evidence={evidence}
                isSuggestion
              />
            ))}
          </>
        ) : (
          <MappingsSectionItem>
            No suggested controls found for this evidence
          </MappingsSectionItem>
        )}
      </MappingsSection>
    </>
  )
}

export const EvidenceControlMappings = ({
  mapping,
}: {
  mapping: EvidenceToControlMappingInfo
}): JSX.Element | null => {
  const evidence = mapping.getEvidence()
  if (!evidence) return null

  return (
    <MappingsContainer>
      <EvidenceControlMappingLinkedControls
        mapping={mapping}
        evidence={evidence}
      />
      <EvidenceControlMappingSuggestedControls
        mapping={mapping}
        evidence={evidence}
      />
    </MappingsContainer>
  )
}
